import { useEffect, useRef, useState } from 'react';
import { TrendingDown, MoreHorizontal, Bell, ChevronRight } from 'lucide-react';
import { STATUS_CONFIG, type Bill } from '../types/bill';
import { caseStatusLine, caseTitle, casePill } from '../lib/case-summary';

type BillRowProps = {
  bill: Bill;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  unreadCount?: number;
};

const DELETABLE_STATUSES = new Set(['submitted', 'info_needed']);

/* v3 list row — replaces the chunky 2-col card grid with a dense, scannable
   table-like layout. Each row has consistent height regardless of whether the
   case has settled amounts yet (Verdict pending appears in the same slot the
   savings chip would). */
export default function BillRow({
  bill,
  onClick,
  onEdit,
  onDelete,
  unreadCount = 0,
}: BillRowProps) {
  const billAmount = bill.bill_amount || 0;
  const amountSaved = bill.amount_saved || 0;
  const currentAmount = billAmount - amountSaved;
  const pct = billAmount > 0 ? Math.round((amountSaved / billAmount) * 100) : 0;
  const title = caseTitle(bill);
  const statusCfg = STATUS_CONFIG[bill.status];
  const canDelete = DELETABLE_STATUSES.has(bill.status);
  const pill = casePill(bill);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <div
      className={`bill-row ${unreadCount > 0 ? 'bill-row-unread' : ''}`}
      onClick={onClick}
      role="listitem"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Title + date */}
      <div className="bill-row-main">
        <div className="bill-row-title-line">
          <span className="bill-row-title">{title}</span>
          {unreadCount > 0 && (
            <span
              className="bill-row-unread-badge"
              title={`${unreadCount} new update${unreadCount === 1 ? '' : 's'}`}
            >
              <Bell size={10} />
              {unreadCount}
            </span>
          )}
        </div>
        <div className="bill-row-meta">
          <span className={`bill-row-status ${statusCfg.className}`}>
            {caseStatusLine(bill)}
          </span>
          <span className="bill-row-meta-dot" aria-hidden>·</span>
          <span className="bill-row-date">
            {new Date(bill.created_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </div>
      </div>

      {/* Amount column */}
      <div className="bill-row-amount">
        {billAmount > 0 ? (
          amountSaved > 0 ? (
            <div className="bill-row-savings">
              <span className="bill-row-savings-amount num">
                <TrendingDown size={13} aria-hidden />
                ${amountSaved.toLocaleString()}
              </span>
              <span className="bill-row-savings-meta">
                {pct}% off ${billAmount.toLocaleString()}
              </span>
            </div>
          ) : (
            <div className="bill-row-savings bill-row-savings--neutral">
              <span className="bill-row-savings-amount num">
                ${currentAmount.toLocaleString()}
              </span>
              <span className="bill-row-savings-meta">On the bill</span>
            </div>
          )
        ) : (
          <span className="bill-row-pending">Amount not read yet</span>
        )}
      </div>

      <span className={`ml-pill ml-pill--${pill.tone} bill-row-pill`}>{pill.label}</span>

      {/* Actions */}
      <div className="bill-row-actions" onClick={(e) => e.stopPropagation()}>
        <div className="bill-menu-wrap" ref={menuRef}>
          <button
            className="bill-menu-btn"
            onClick={() => setMenuOpen((s) => !s)}
            aria-label="Case actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && (
            <div className="bill-menu" role="menu">
              <button
                className="bill-menu-item"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
              >
                Rename
              </button>
              <button
                className="bill-menu-item bill-menu-item--danger"
                role="menuitem"
                disabled={!canDelete}
                onClick={() => {
                  if (!canDelete) return;
                  setMenuOpen(false);
                  onDelete();
                }}
                title={
                  canDelete
                    ? undefined
                    : "We've already started on this case, so it can't be deleted"
                }
              >
                Delete
              </button>
            </div>
          )}
        </div>
        <ChevronRight size={16} className="bill-row-chevron" aria-hidden />
      </div>
    </div>
  );
}
